import { useEffect, useRef } from "react";
import classNames from "classnames";
import Box from "@mui/material/Box";

const BAR_MIN_HEIGHT = 6;
const BAR_MAX_HEIGHT = 20;
const DOT_SIZE = 4;
const BAR_WEIGHTS = [0.3, 0.5, 0.75, 1.0, 1.0, 0.75, 0.5, 0.3];

interface AudioWaveformProps {
  analyserNode: AnalyserNode | null;
  colorClass?: string;
}

const AudioWaveform = ({
  analyserNode,
  colorClass = "bg-red-400",
}: AudioWaveformProps) => {
  const barRefs = useRef<(HTMLDivElement | null)[]>([]);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!analyserNode) {
      barRefs.current.forEach((bar) => {
        if (bar) bar.style.height = `${DOT_SIZE}px`;
      });

      return;
    }

    const dataArray = new Uint8Array(analyserNode.frequencyBinCount);

    const update = () => {
      analyserNode.getByteTimeDomainData(dataArray);

      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const normalized = (dataArray[i] - 128) / 128;
        sum += normalized * normalized;
      }
      const loudness = Math.min(1, Math.sqrt(sum / dataArray.length) * 8);

      for (let i = 0; i < BAR_WEIGHTS.length; i++) {
        const bar = barRefs.current[i];
        if (bar) {
          const height =
            BAR_MIN_HEIGHT +
            loudness * BAR_WEIGHTS[i] * (BAR_MAX_HEIGHT - BAR_MIN_HEIGHT);
          bar.style.height = `${height}px`;
        }
      }

      rafRef.current = requestAnimationFrame(update);
    };

    rafRef.current = requestAnimationFrame(update);

    return () => cancelAnimationFrame(rafRef.current);
  }, [analyserNode]);

  return (
    <Box className="flex items-center justify-center gap-0.75 h-full">
      {BAR_WEIGHTS.map((_, i) => (
        <div
          key={i}
          ref={(el) => {
            barRefs.current[i] = el;
          }}
          className={classNames(
            "rounded-full w-1 transition-all duration-50 ease-out",
            colorClass
          )}
          style={{
            height: `${analyserNode ? BAR_MIN_HEIGHT : DOT_SIZE}px`,
          }}
        />
      ))}
    </Box>
  );
};

export default AudioWaveform;
